import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { TreePicker } from '@ipfabric/ui-components/TreePicker/TreePicker';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { Controller } from 'react-hook-form';
import { useNotifications } from 'utils/hooks/useNotifications';
import { z } from 'zod';
import { useOasEndpointsTreePickerItems } from '../Policies/useOasEndpointsTreePickerItems';
import { type PolicyFormMode, canSaveInMode } from '../shared/policyFormMode';
import { useWriteScopes } from '../shared/useWriteScopes';
import {
  type EndpointPolicy,
  useEndpointPoliciesApi,
} from './endpointPoliciesApi';

export type EndpointPolicyFormMode = PolicyFormMode;

const endpointPolicyFormSchema = z.object({
  name: z.string().trim().min(1, 'Policy name is required.').max(255),
  description: z.string().max(2000),
  apiScopeIds: z.array(z.string()),
});

type EndpointPolicyFormValues = z.infer<typeof endpointPolicyFormSchema>;

export const ENDPOINT_POLICY_TITLE_BY_MODE: Record<
  EndpointPolicyFormMode,
  string
> = {
  add: 'Add endpoint policy',
  edit: 'Edit endpoint policy',
  duplicate: 'Copy endpoint policy',
  view: 'Endpoint policy detail',
};

// Same endpoint tree builder and titles as the legacy API policy form.
const TREE_PICKER_MSG = {
  availableTitle: 'Available endpoints',
  selectedTitle: 'Selected endpoints',
};

type EndpointPolicyFormProps = {
  mode: EndpointPolicyFormMode;
  policy?: EndpointPolicy;
  onClose: () => void;
};

export const EndpointPolicyForm = ({
  mode,
  policy,
  onClose,
}: EndpointPolicyFormProps) => {
  const scopes = useWriteScopes('/endpoint-policies', '/endpoint-policies/:id');
  // Without the write scope for this mode the panel only shows the record.
  const readOnly = !canSaveInMode(mode, scopes);
  const items = useOasEndpointsTreePickerItems();
  const { showNotification } = useNotifications();
  const { createEndpointPolicy, updateEndpointPolicy } =
    useEndpointPoliciesApi();

  const form = useCustomForm<EndpointPolicyFormValues>({
    defaultValues: {
      name:
        mode === 'duplicate' && policy
          ? `Copy of ${policy.name}`
          : (policy?.name ?? ''),
      description: policy?.description ?? '',
      apiScopeIds: policy?.apiScopeIds ?? [],
    },
    resolver: zodResolver(endpointPolicyFormSchema),
  });
  const { control, formState, handleSubmit } = form;

  const onSubmit = handleSubmit(async (values) => {
    if (mode === 'edit' && policy) {
      await updateEndpointPolicy(policy.id, values);
    } else {
      await createEndpointPolicy(values);
    }
    showNotification({
      type: 'success',
      message:
        mode === 'edit' ? 'Endpoint policy updated' : 'Endpoint policy created',
    });
    onClose();
  });

  return (
    <form className='flex flex-1 flex-col' onSubmit={onSubmit}>
      <div className='flex flex-row gap-8 pt-5'>
        <div className='flex-1'>
          <FormTextField
            control={control}
            dataTestId='endpoint-policy-name'
            disabled={readOnly}
            label='Policy name'
            name='name'
            type='text'
          />
        </div>
        <div className='flex-1'>
          <FormTextField
            control={control}
            dataTestId='endpoint-policy-description'
            disabled={readOnly}
            label='Description'
            name='description'
            type='text'
          />
        </div>
      </div>
      <div className='grow'>
        <div className='form__label'>Select endpoints for policy</div>
        <Controller
          control={control}
          name='apiScopeIds'
          render={({ field }) => (
            <TreePicker
              disabled={readOnly}
              items={items}
              msg={TREE_PICKER_MSG}
              value={new Set(field.value)}
              onChange={(value) => field.onChange([...value])}
            />
          )}
        />
      </div>
      <div className='mt-8 flex gap-xs'>
        {!readOnly && (
          <Button
            dataTestId='endpoint-policy-save'
            icon='fa-floppy-disk'
            loading={formState.isSubmitting}
            onClick={() => void onSubmit()}
            type='button'
            variant='primary'
          >
            Save
          </Button>
        )}
        <Button
          dataTestId='endpoint-policy-close'
          onClick={onClose}
          type='button'
          variant='outline'
        >
          {readOnly ? 'Back' : 'Cancel'}
        </Button>
      </div>
    </form>
  );
};
